import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose, IconDownload, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { downloadListCsv } from '../utils/csv';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/ProcurementPayments.jsx. This is
// a VendorInvoice list (GET /vendor-invoices) — the backend has no separate
// GET /procurement-payments index route at all (see routes/api.php;
// ProcurementPayment only gets a `documents` sub-route), so "Payments" here
// means vendor invoices and the payment vouchers/payments raised against
// them, exactly as the source app treats it.
// `labelFor(lists, 'finance_method', ...)` -> `labelFor(lists,
// 'payment_method', ...)`, see ProcurementInvoiceForm.tsx's note.
const STATUS_LABELS: Record<string, string> = {
  pending_verification: 'Pending Verification',
  matched_to_po: 'Waiting Approval',
  discrepancy_found: 'Discrepancy Found',
  approved_for_payment: 'Approved for Payment',
  paid: 'Paid',
  rejected: 'Rejected',
};

function statusBadgeClass(status: string): string {
  if (status === 'paid') return 'badge-normal';
  if (['discrepancy_found', 'rejected'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

interface VendorInvoiceRow {
  id: number;
  payment_number: string | null;
  invoice_number: string | null;
  invoice_amount: number;
  payment_method: string | null;
  status: string;
  vendor_name: string;
  category: string | null;
  purchase_order: { po_number: string; vendor_name: string } | null;
  payment_voucher: { pv_number: string; payment: { id: number } | null; status: string } | null;
}

function paymentVoucherStatus(invoice: VendorInvoiceRow): string | null {
  const pv = invoice.payment_voucher;
  if (!pv) return null;
  if (pv.payment) return 'Payment recorded';
  if (pv.status === 'approved_for_payment') return 'Voucher approved, awaiting payment';
  return null;
}

export function ProcurementPayments() {
  const { settings, lists } = useConfig();
  const { can } = useAuth();
  const canApprovePayments = can('procurement.approve');
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({ status: status || undefined, search: search || undefined }),
    [status, search]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<VendorInvoiceRow>(
    '/vendor-invoices',
    filters
  );

  const decideInvoice = async (inv: VendorInvoiceRow, decision: 'approved' | 'rejected') => {
    await api.post(`/vendor-invoices/${inv.id}/approve`, { decision });
    load();
  };

  const exportEft = () => downloadFile(api, '/payment-vouchers/export-eft', `ap-payments-eft-${new Date().toISOString().slice(0, 10)}.csv`);

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<VendorInvoiceRow>(
      api,
      '/vendor-invoices',
      { status: status || undefined, search: search || undefined },
      [
        { label: 'Invoice Number', value: (r) => r.invoice_number ?? '' },
        { label: 'Vendor', value: (r) => r.purchase_order?.vendor_name ?? r.vendor_name },
        { label: 'PO Number', value: (r) => r.purchase_order?.po_number ?? '' },
        { label: 'Category', value: (r) => r.category ?? '' },
        { label: 'Amount', value: (r) => r.invoice_amount },
        { label: 'Status', value: (r) => STATUS_LABELS[r.status] ?? r.status },
        { label: 'Payment Status', value: (r) => paymentVoucherStatus(r) ?? '' },
      ],
      'vendor-invoices.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Payments</h1>
        <div className="page-header-actions">
          {canApprovePayments && (
            <IconButton icon={IconDownload} label="Export EFT CSV" onClick={exportEft} />
          )}
          <Link className="btn btn-primary" to="/procurement/payments/new">
            + Add Payments
          </Link>
        </div>
      </header>
      <p className="muted">Vendor invoices and the payment vouchers and payments raised against them.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search invoice number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Payment No</th>
                <th>Order Number</th>
                <th>Invoice Number</th>
                <th>Payment Method</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((inv) => (
                  <tr key={inv.id}>
                    <td className="muted">{inv.payment_number ?? inv.payment_voucher?.pv_number ?? '—'}</td>
                    <td className="muted">{inv.purchase_order?.po_number ?? '—'}</td>
                    <td className="muted">{inv.invoice_number ?? '—'}</td>
                    <td className="muted">
                      {inv.payment_method ? labelFor(lists, 'payment_method', inv.payment_method) : '—'}
                    </td>
                    <td className="right">{fmtMoney(inv.invoice_amount)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(inv.status)}`}>
                        {STATUS_LABELS[inv.status] ?? inv.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton
                        icon={IconClose}
                        label="Reject"
                        variant="danger-ghost"
                        onClick={() => decideInvoice(inv, 'rejected')}
                        hidden={!['matched_to_po', 'discrepancy_found'].includes(inv.status) || !canApprovePayments}
                      />
                      <IconButton
                        icon={IconCheck}
                        label="Approve for payment"
                        onClick={() => decideInvoice(inv, 'approved')}
                        hidden={!['matched_to_po', 'discrepancy_found'].includes(inv.status) || !canApprovePayments}
                      />
                      <IconButton icon={IconView} label="View" to={`/procurement/payments/${inv.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No vendor invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
